import { useState } from 'react'
import { cn } from '@/lib/cn'

interface TagEditorProps {
  tags: string[]
  onChange: (tags: string[]) => void
  className?: string
  label: string
}

/**
 * The add-a-chip control shared by UploadMedia (tags) and the custom-game form
 * (genres, platforms) — three near-identical copies in the original, each with
 * its own `adding_x` / `add_x` pair of data properties.
 */
export function TagEditor({ tags, onChange, className, label }: TagEditorProps) {
  const [isAdding, setIsAdding] = useState(false)
  const [draft, setDraft] = useState('')

  const commit = () => {
    const value = draft.trim()
    if (value.length > 0 && !tags.includes(value)) onChange([...tags, value])
    setDraft('')
    setIsAdding(false)
  }

  return (
    <div className={cn('tags', className)} role="group" aria-label={label}>
      {tags.map((tag) => (
        <button
          key={tag}
          type="button"
          className="tag removable"
          aria-label={`Remove ${tag}`}
          onClick={() => onChange(tags.filter((entry) => entry !== tag))}
        >
          {tag}
          <svg viewBox="0 0 12 12" aria-hidden="true" className="tag-remove">
            <path d="M3.5 3.5l5 5M8.5 3.5l-5 5" />
          </svg>
        </button>
      ))}

      {isAdding ? null : (
        <button type="button" className="tag add button" onClick={() => setIsAdding(true)}>
          <svg viewBox="0 0 12 12" aria-hidden="true" className="tag-add-glyph">
            <path d="M6 2.5v7M2.5 6h7" />
          </svg>
          Add {label}
        </button>
      )}

      {isAdding ? (
        <input
          className="tag input"
          autoFocus
          value={draft}
          aria-label={`New ${label}`}
          placeholder={`New ${label}`}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              commit()
            }
            if (event.key === 'Escape') {
              setDraft('')
              setIsAdding(false)
            }
          }}
        />
      ) : null}
    </div>
  )
}
